import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, num, fmtDate } from "../lib/api";
import { useAuth } from "../lib/auth";
import { save } from "../hooks/useData";
import { PageHeader, Button, DataTable, KpiCard, Modal, Field, Input, Textarea } from "../components/shared";
import { ITEM_TYPES } from "./Cutting";
import { LotRemarks } from "./Wip";
import { FileCheck2, PackageCheck, Shuffle, Pencil, XCircle, AlertTriangle } from "lucide-react";

const fcls = "border border-slate-300 rounded px-2 py-1.5 text-sm h-9";
const BLANK = { from: "", to: "", design: "", item: "" };

function EditLotModal({ edit, setEdit, onSaved }) {
  if (!edit) return null;
  const sum = ["fresh_qty", "mix_qty", "reject_qty", "shortage_qty"].reduce((a, k) => a + (parseFloat(edit[k]) || 0), 0);
  const ok = Math.abs(sum - (edit.cleared_qty || 0)) < 0.001;
  const submit = async (e) => {
    e.preventDefault();
    if (!ok) { toast.error(`Fresh + Mix + Reject + Shortage must equal ${num(edit.cleared_qty)}`); return; }
    try {
      await save("put", `/finishing-clearances/${edit.id}`, { slip_number: edit.slip_number, date: edit.date, remarks: edit.remarks,
        fresh_qty: parseFloat(edit.fresh_qty) || 0, mix_qty: parseFloat(edit.mix_qty) || 0, reject_qty: parseFloat(edit.reject_qty) || 0, shortage_qty: parseFloat(edit.shortage_qty) || 0 });
      toast.success("Cleared lot updated"); setEdit(null); onSaved();
    } catch { /* save() shows the error */ }
  };
  const set = (k, v) => setEdit({ ...edit, [k]: v });
  return (
    <Modal open onClose={() => setEdit(null)} title={`Edit Cleared Lot \u2014 ${edit.design_number}`}>
      <form onSubmit={submit} className="grid grid-cols-2 gap-4">
        <Field label="Slip No" required><Input value={edit.slip_number} onChange={(e) => set("slip_number", e.target.value)} required data-testid="cl-edit-slip" /></Field>
        <Field label="Slip Date"><Input type="date" value={edit.date || ""} onChange={(e) => set("date", e.target.value)} data-testid="cl-edit-date" /></Field>
        <div className="col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Field label="Fresh Qty"><Input type="number" step="any" min="0" value={edit.fresh_qty} onChange={(e) => set("fresh_qty", e.target.value)} data-testid="cl-edit-fresh" /></Field>
          <Field label="Mix Qty"><Input type="number" step="any" min="0" value={edit.mix_qty} onChange={(e) => set("mix_qty", e.target.value)} data-testid="cl-edit-mix" /></Field>
          <Field label="Reject Qty"><Input type="number" step="any" min="0" value={edit.reject_qty} onChange={(e) => set("reject_qty", e.target.value)} data-testid="cl-edit-reject" /></Field>
          <Field label="Shortage"><Input type="number" step="any" min="0" value={edit.shortage_qty} onChange={(e) => set("shortage_qty", e.target.value)} data-testid="cl-edit-shortage" /></Field>
          <p className={`col-span-2 sm:col-span-4 text-xs ${ok ? "text-slate-400" : "text-red-600"}`} data-testid="cl-edit-sum">Fresh + Mix + Reject + Shortage = {num(sum)} (must equal the {num(edit.cleared_qty)} pcs cleared)</p>
        </div>
        <Field label="Remarks" className="col-span-2"><Textarea value={edit.remarks || ""} onChange={(e) => set("remarks", e.target.value)} data-testid="cl-edit-remarks" /></Field>
        <div className="col-span-2 flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={() => setEdit(null)} data-testid="cl-edit-cancel">Cancel</Button>
          <Button type="submit" disabled={!ok} data-testid="cl-edit-save">Save</Button>
        </div>
      </form>
    </Modal>
  );
}

export default function ClearedSlips() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "Admin";
  const [rows, setRows] = useState([]);
  const [f, setF] = useState(BLANK);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);

  const load = useCallback(async (flt) => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/cleared-slips", { params: { from_date: flt.from || undefined, to_date: flt.to || undefined, design: flt.design || undefined, item: flt.item || undefined } });
      setRows(data || []);
    } catch { toast.error("Failed to load cleared lots"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(BLANK); }, [load]);

  const undo = async (s) => {
    if (!window.confirm(`Undo slip ${s.slip_number}? ${num(s.cleared_qty)} pcs go back into Finishing WIP.`)) return;
    try { await api.delete(`/finishing-clearances/${s.id}`); toast.success(`Slip ${s.slip_number} undone`); load(f); }
    catch (e) { toast.error(e.response?.data?.detail || "Undo failed"); }
  };

  const total = (k) => rows.reduce((a, r) => a + (Number(r[k]) || 0), 0);
  const columns = [
    { key: "date", header: "Slip Date", render: (r) => fmtDate(r.date) },
    { key: "slip_number", header: "Slip No", render: (r) => <span className="font-mono font-medium text-blue-800">{r.slip_number}</span> },
    { key: "cutting_date", header: "Cutting Date", render: (r) => (r.cutting_date ? r.cutting_date.split(", ").map(fmtDate).join(", ") : "\u2014") },
    { key: "cutting_sheet_number", header: "Cutting Sheet No", render: (r) => r.cutting_sheet_number || "\u2014" },
    { key: "design_number", header: "Design" },
    { key: "item_type", header: "Item", render: (r) => r.item_type || "\u2014" },
    { key: "fresh_qty", header: "Fresh", align: "right", render: (r) => <span className="font-semibold text-emerald-700">{num(r.fresh_qty)}</span> },
    { key: "mix_qty", header: "Mix", align: "right", render: (r) => num(r.mix_qty) },
    { key: "reject_qty", header: "Reject", align: "right", render: (r) => <span className={r.reject_qty > 0 ? "text-red-600 font-medium" : ""}>{num(r.reject_qty)}</span> },
    { key: "shortage_qty", header: "Shortage", align: "right", render: (r) => <span className={r.shortage_qty > 0 ? "text-red-600 font-medium" : ""}>{num(r.shortage_qty)}</span> },
    { key: "total_qty", header: "Total", align: "right", render: (r) => <span className="font-semibold">{num(r.total_qty)}</span> },
    { key: "remarks", header: "Remarks", render: (r) => <LotRemarks poid={r.production_order_id} lines={r.lot_remark_lines} old={r.remarks} saveOld={(t) => api.put(`/finishing-clearances/${r.id}`, { remarks: t })} onSaved={() => load(f)} tid={`${r.production_order_id}-${r.id}`} /> },
    { key: "act", header: "", sortable: false, render: (r) => (
      <div className="flex items-center gap-2 justify-end" onClick={(e) => e.stopPropagation()}>
        <button className="p-1.5 text-slate-400 hover:text-blue-700" title="Edit" onClick={() => setEdit({ ...r })} data-testid={`cleared-lot-edit-${r.id}`}><Pencil size={14} /></button>
        {isAdmin && <button className="text-red-500 hover:text-red-700 text-xs font-medium" onClick={() => undo(r)} data-testid={`cleared-slip-undo-${r.id}`}>Undo</button>}
      </div>
    ) },
  ];

  return (
    <div>
      <PageHeader title="Cleared Lots" subtitle="Click any cleared lot to see the quantities received from each jobber, its jobber bills, and to record a jobber bill. Total = fresh + mix + reject + shortage." />
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
        <KpiCard testid="cs-kpi-slips" label="Cleared Lots" value={num(rows.length)} icon={FileCheck2} accent="sky" />
        <KpiCard testid="cs-kpi-fresh" label="Fresh Pcs" value={num(total("fresh_qty"))} icon={PackageCheck} accent="emerald" />
        <KpiCard testid="cs-kpi-mix" label="Mix Pcs" value={num(total("mix_qty"))} icon={Shuffle} accent="amber" />
        <KpiCard testid="cs-kpi-reject" label="Reject Pcs" value={num(total("reject_qty"))} icon={XCircle} accent="red" />
        <KpiCard testid="cs-kpi-shortage" label="Shortage Pcs" value={num(total("shortage_qty"))} icon={AlertTriangle} accent="violet" />
      </div>
      <div className="flex flex-wrap items-end gap-2 mb-3">
        <div><label className="block text-xs text-slate-500 mb-1">From</label><input type="date" value={f.from} onChange={(e) => setF({ ...f, from: e.target.value })} className={fcls} data-testid="cs-from" /></div>
        <div><label className="block text-xs text-slate-500 mb-1">To</label><input type="date" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} className={fcls} data-testid="cs-to" /></div>
        <div><label className="block text-xs text-slate-500 mb-1">Design</label><input value={f.design} onChange={(e) => setF({ ...f, design: e.target.value })} placeholder="Design" className={`${fcls} w-32`} data-testid="cs-page-design" /></div>
        <div><label className="block text-xs text-slate-500 mb-1">Item</label>
          <select value={f.item} onChange={(e) => setF({ ...f, item: e.target.value })} className={`${fcls} w-36`} data-testid="cs-page-item">
            <option value="">All items</option>{ITEM_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select></div>
        <Button size="sm" onClick={() => load(f)} data-testid="cs-apply">Apply</Button>
        <Button size="sm" variant="secondary" data-testid="cs-clear" onClick={() => { setF(BLANK); load(BLANK); }}>Clear</Button>
      </div>
      {loading ? <div className="h-40 grid place-items-center"><div className="h-8 w-8 border-4 border-blue-800 border-t-transparent rounded-full animate-spin" /></div>
        : <DataTable testid="cleared-slips-table" columns={columns} rows={rows} exportName="cleared-lots.csv" onRowClick={(r) => navigate(`/wip?lot=${r.production_order_id}&from=cleared`)} />}
      <EditLotModal edit={edit} setEdit={setEdit} onSaved={() => load(f)} />
    </div>
  );
}
